import classNames from 'classnames'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'

import usePartnerOffer from '~/campaigns/hooks/usePartnerOffer'
import { PartnerDetailsType } from '~/campaigns/types/campaign.types'
import { useGetStripePricesQuery } from '~/subscription/apis/subscription.api'
import { StripePrice } from '~/subscription/types/subscription.types'
import { formatToLocalCurrency } from '~/subscription/util'
import { isKnowItPrice } from '~/subscription/utils/withoutKnowItPrices'
import { AppLanguage } from '~/types/locale'

interface Props {
  partnerDetails: PartnerDetailsType
  className?: string
}

interface BadgeCopy {
  prefix?: string
  value: string
  label: string
}

const MONTHS_PER_YEAR = 12

const isFamilyYearly = (price?: StripePrice): boolean =>
  price?.metadata?.subscriptionType === 'family' && price?.recurring?.interval === 'year' && !isKnowItPrice(price)

/** The partner's offer as a tilted ticket in the landing hero: one discount, or "up to" two, from the yearly price. */
export default function PartnerOfferBadge({ partnerDetails, className = '' }: Props): React.ReactElement | null {
  const { t } = useTranslation('campaign', { keyPrefix: 'offerBadge' })
  const language = useRouter().locale as AppLanguage
  const offer = usePartnerOffer(partnerDetails, 1)
  const { data: geoPrices } = useGetStripePricesQuery({ excludeDnb: true })
  const geoYearly: StripePrice | undefined = geoPrices?.stripeGeoLocationPricesWithCouponDetails
    ?.map((entry: { price: StripePrice }) => entry.price)
    .find(isFamilyYearly)

  const yearlyBase = offer.yearly.base ?? (geoYearly?.tiers?.[0]?.unit_amount ?? 0) / 100
  const currency = offer.yearly.base != null ? offer.currency : geoYearly?.currency

  const monthlyOff = offer.monthly.percentOff ?? 0
  const yearlyOff = offer.yearly.percentOff ?? 0
  // The yearly price per month after the partner's yearly discount.
  const fromPrice = t('fromPrice', {
    price: formatToLocalCurrency(currency ?? '', ((yearlyBase / MONTHS_PER_YEAR) * (100 - yearlyOff)) / 100, language)
      .priceLabel,
  })
  const maxOff = Math.max(monthlyOff, yearlyOff)

  const copy: BadgeCopy | undefined =
    maxOff && monthlyOff === yearlyOff
      ? { value: `${monthlyOff}%`, label: t('off') }
      : maxOff
      ? { prefix: t('upTo'), value: `${maxOff}%`, label: t('discount') }
      : undefined

  // Hidden until the price it quotes has loaded, so the pill never shows a zero.
  if (!partnerDetails.showOfferBadge || !copy || !yearlyBase || offer.isLoading) return null

  return (
    <div className={classNames('rotate-7 select-none', className)}>
      <div className="relative rounded-20 border-1.5 border-dashed border-white p-1.5">
        <div
          className={classNames(
            'flex items-center gap-1.5 rounded-14 bg-gradient-partner-badge pl-7 pr-5.5 pt-4 text-white drop-shadow-partner-badge',
            copy.prefix ? 'pb-8.5' : 'pb-5.5',
          )}
        >
          <div className="flex flex-col whitespace-nowrap leading-none">
            {copy.prefix && (
              <span className="-mb-1 text-20px font-extrabold tracking-0.04em text-green-lime-soft">{copy.prefix}</span>
            )}
            <span className="text-7xl font-black">{copy.value}</span>
          </div>
          <span className="flex w-5.5 items-center justify-center self-stretch">
            <span className="-rotate-90 whitespace-nowrap text-22px font-extrabold leading-0.95">{copy.label}</span>
          </span>
        </div>
        <p className="absolute -bottom-3.5 left-14 whitespace-nowrap rounded-full border-2.5 border-purple-badge-ink bg-green-lime-soft px-5 py-2 text-20px font-extrabold leading-none text-purple-badge-ink">
          {fromPrice}
        </p>
      </div>
    </div>
  )
}
